if (!customElements.get('ds-labs-navigation')) {
  customElements.define('ds-labs-navigation', class extends HTMLElement {
    // Grace period before a hover-opened dropdown closes, so small jitters don't dismiss it.
    static CLOSE_DELAY = 150;

    connectedCallback() {
      this.controller?.abort();
      this.cancelClose();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.hoverQuery = matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

      // Desktop mouse, tracked by cursor position rather than per-element enter events:
      // - over a category name: open its dropdown and close the others;
      // - on the open category, anywhere below it down to the list, or on the list: keep it open;
      // - anywhere else (left, right, above, off the list): close after a short delay.
      const onPointer = event => {
        if (!this.isMouseHover(event)) return;
        const x = event.clientX;
        const y = event.clientY;
        const hovered = this.categoryAt(x, y);
        if (hovered) {
          this.cancelClose();
          if (!hovered.open) {
            this.closeMenus(hovered);
            hovered.open = true;
          }
          return;
        }
        const open = this.querySelector('details[open]');
        if (!open) return;
        if (this.isInsideMenu(open, x, y)) this.cancelClose();
        else this.scheduleClose();
      };
      ['pointermove', 'mousemove'].forEach(type => document.addEventListener(type, onPointer, options));
      ['pointerover', 'mouseover'].forEach(type => this.addEventListener(type, onPointer, options));
      ['pointerleave', 'mouseleave'].forEach(type => document.documentElement.addEventListener(type, event => {
        if (this.isMouseHover(event)) this.scheduleClose();
      }, options));

      this.addEventListener('click', event => {
        const summary = event.target.closest('summary');
        if (summary && this.contains(summary)) {
          // A mouse click on a hover-opened category keeps it open instead of toggling it shut.
          if (summary.parentElement.open && this.hoverQuery.matches && event.detail > 0) {
            event.preventDefault();
            return;
          }
          if (!summary.parentElement.open) {
            this.cancelClose();
            this.closeMenus(summary.parentElement);
          }
        }
        if (event.target.closest('.ds-panel a')) this.closeMenus();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          const open = this.querySelector('details[open]');
          if (open) {
            event.preventDefault();
            this.cancelClose();
            this.closeMenus();
            open.querySelector('summary').focus();
          }
        }
      }, options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closeMenus();
      }, options);
      this.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeMenus();
      }, options);
      this.hoverQuery.addEventListener('change', () => {
        this.cancelClose();
        this.closeMenus();
      }, options);
    }

    isMouseHover(event) {
      // Mouse events have no pointerType; pointer events must come from a mouse, not touch or pen.
      const type = event.pointerType;
      return this.hoverQuery.matches && (type === undefined || type === '' || type === 'mouse');
    }

    categoryAt(x, y) {
      for (const summary of this.querySelectorAll('.ds-category > summary')) {
        const rect = summary.getBoundingClientRect();
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return summary.parentElement;
      }
      return null;
    }

    isInsideMenu(item, x, y) {
      const summary = item.querySelector('summary').getBoundingClientRect();
      const panel = item.querySelector('.ds-panel')?.getBoundingClientRect();
      const within = rect => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      if (within(summary)) return true;
      if (!panel) return false;
      if (within(panel)) return true;
      // Bridge any gap between the category name and the top of the list.
      return y > summary.bottom && y < panel.top && x >= panel.left && x <= panel.right;
    }

    scheduleClose() {
      if (this.closeTimer) return;
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        this.closeMenus();
      }, this.constructor.CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    closeMenus(except) {
      this.querySelectorAll('details[open]').forEach(item => {
        if (item !== except) item.open = false;
      });
    }

    disconnectedCallback() {
      this.cancelClose();
      this.controller?.abort();
    }
  });
}
